import throttle from 'lodash/throttle';
import {
  MouseEvent,
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Country } from 'enums';
import { Address, InlineAddress } from 'types';
import { formatOneLineAddressWithoutCountry } from 'utils/formatters';
import { AddressLookupInlineProps } from './AddressLookupInlineProps';
import {
  EMPTY_PREDICTION,
  buildPredictionFromManualAddress,
  ensureGoogleMapsScriptTag,
  getGoogleServicesIfReady,
  hasCompleteAddress,
  makePredictionFromDescription,
  mapPlaceResultToInlineAddress,
} from './google-places/google-places.utils';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type PlaceResult = google.maps.places.PlaceResult;

const AUTO_RESOLVE_MAX_RETRIES = 3;
const AUTO_RESOLVE_RETRY_DELAY_MS = 300;
const PREDICTIONS_THROTTLE_MS = 200;

interface AutoResolveSuccess {
  address: InlineAddress;
  canonicalDisplay: string;
  prediction: AutocompletePrediction;
}

interface UseAutoResolveAddressArgs {
  enabled: boolean;
  sourceValue: string;
  hostElementRef: RefObject<HTMLElement>;
  onResolved: (success: AutoResolveSuccess) => void;
  onIncomplete?: (partialAddress: InlineAddress) => void;
  onFailed: () => void;
}

// AI-prefill > Google Places > structured fields flow
// Returns `isPending: true` until Google resolves an address
const useAutoResolveAddress = ({
  enabled,
  sourceValue,
  hostElementRef,
  onResolved,
  onIncomplete,
  onFailed,
}: UseAutoResolveAddressArgs): { isPending: boolean } => {
  const attemptedRef = useRef(false);
  const retryCountRef = useRef(0);
  const [isPending, setIsPending] = useState(
    () => enabled && Boolean(sourceValue)
  );

  const onResolvedRef = useRef(onResolved);
  const onIncompleteRef = useRef(onIncomplete);
  const onFailedRef = useRef(onFailed);
  onResolvedRef.current = onResolved;
  onIncompleteRef.current = onIncomplete;
  onFailedRef.current = onFailed;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!sourceValue) {
      attemptedRef.current = false;
      retryCountRef.current = 0;
      setIsPending(false);
      return;
    }

    if (attemptedRef.current) {
      return;
    }

    setIsPending(true);

    let cancelled = false;
    let retryTimeoutId: number | undefined;

    const succeed = (success: AutoResolveSuccess): void => {
      setIsPending(false);
      onResolvedRef.current(success);
    };
    const fail = (): void => {
      setIsPending(false);
      onFailedRef.current();
    };
    const incomplete = (partialAddress: InlineAddress): void => {
      setIsPending(false);
      if (onIncompleteRef.current) {
        onIncompleteRef.current(partialAddress);
      } else {
        onFailedRef.current();
      }
    };

    const runAttempt = (): void => {
      if (cancelled) {
        return;
      }

      const services = getGoogleServicesIfReady(hostElementRef.current);

      if (!services) {
        if (retryCountRef.current < AUTO_RESOLVE_MAX_RETRIES) {
          retryCountRef.current += 1;
          retryTimeoutId = window.setTimeout(
            runAttempt,
            AUTO_RESOLVE_RETRY_DELAY_MS
          );
        } else {
          fail();
        }
        return;
      }

      attemptedRef.current = true;

      services.autocomplete.getPlacePredictions(
        {
          input: sourceValue,
          componentRestrictions: {
            country: [Country.Australia, Country.NewZealand],
          },
        },
        (results: AutocompletePrediction[] | null) => {
          const top = results?.[0];
          if (!top?.place_id) {
            fail();
            return;
          }

          services.places.getDetails(
            { placeId: top.place_id, fields: ['address_components'] },
            (details: PlaceResult | null) => {
              if (!details?.address_components) {
                fail();
                return;
              }

              const address = mapPlaceResultToInlineAddress(details);
              if (!hasCompleteAddress(address)) {
                incomplete(address);
                return;
              }

              succeed({
                address,
                canonicalDisplay: formatOneLineAddressWithoutCountry(address),
                prediction: top,
              });
            }
          );
        }
      );
    };

    runAttempt();

    return (): void => {
      cancelled = true;
      if (retryTimeoutId !== undefined) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [enabled, sourceValue]);

  return { isPending };
};

interface UseAddressLookupArgs
  extends Pick<
    AddressLookupInlineProps,
    'value' | 'error' | 'handleChange' | 'manualAddress' | 'autoResolve'
  > {
  hostElementRef: RefObject<HTMLDivElement>;
}

export interface UseAddressLookupResult {
  autoCompleteValue: AutocompletePrediction;
  inputValue: string;
  options: AutocompletePrediction[];
  selectedAddress: InlineAddress | undefined;
  manualEntryOpen: boolean;
  handleAutoCompleteChange: (
    event: SyntheticEvent,
    newValue: string | AutocompletePrediction | null
  ) => void;
  handleInputChange: (
    event: SyntheticEvent | null,
    newInputValue: string
  ) => void;
  setIsManualEntryOpen: (event: MouseEvent, isOpen: boolean) => void;
  syncManualAddress: (nextAddress: InlineAddress) => void;
}

export const useAddressLookup = ({
  value,
  error,
  handleChange,
  manualAddress,
  autoResolve = false,
  hostElementRef,
}: UseAddressLookupArgs): UseAddressLookupResult => {
  // Kick off the Google Maps script exactly once
  const scriptLoadedRef = useRef(false);
  if (!scriptLoadedRef.current && typeof window !== 'undefined') {
    ensureGoogleMapsScriptTag(process.env.REACT_APP_GOOGLE_API_KEY ?? '');
    scriptLoadedRef.current = true;
  }

  // Google Places state
  const [autoCompleteValue, setAutoCompleteValue] =
    useState<AutocompletePrediction>(() =>
      makePredictionFromDescription(value ?? '')
    );
  const [inputValue, setInputValue] = useState<string>(value ?? '');
  const [options, setOptions] = useState<AutocompletePrediction[]>([]);

  // Manual-entry panel state
  const [manualEntryOpen, setManualEntryOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<InlineAddress>();

  // -------------------------------------------------------------------------
  // Forwards single-line address from parent to Google before displaying
  // On complete success (all address fields exist) we reveal Google's canonical form
  // On partial success we open the manual panel prefilled with the fields we did get
  // On failure we clear the field.
  // -------------------------------------------------------------------------
  const revealResolvedAddress = useCallback(
    (
      prediction: AutocompletePrediction,
      canonicalDisplay: string,
      address: InlineAddress
    ) => {
      setAutoCompleteValue({ ...prediction, description: canonicalDisplay });
      setInputValue(canonicalDisplay);
      setSelectedAddress(address);
      handleChange(null, address as Address);
    },
    [handleChange]
  );

  const notifyResolutionFailed = useCallback(() => {
    handleChange(null, null);
  }, [handleChange]);

  const notifyResolutionIncomplete = useCallback(
    (partialAddress: InlineAddress) => {
      setSelectedAddress(partialAddress);
      setManualEntryOpen(true);
    },
    []
  );

  const { isPending: isAutoResolvePending } = useAutoResolveAddress({
    enabled: autoResolve,
    sourceValue: value ?? '',
    hostElementRef,
    onResolved: ({ prediction, canonicalDisplay, address }) =>
      revealResolvedAddress(prediction, canonicalDisplay, address),
    onIncomplete: notifyResolutionIncomplete,
    onFailed: notifyResolutionFailed,
  });

  useEffect(() => {
    const nextValue = value ?? '';
    const displayValue = isAutoResolvePending ? '' : nextValue;

    setAutoCompleteValue(makePredictionFromDescription(displayValue));
    setInputValue(displayValue);
  }, [value, isAutoResolvePending]);

  // -------------------------------------------------------------------------
  // Auto-open the manual-entry panel when the parent form fails validation
  // AND we already have partial address data to prefill the fields with.
  // If there's no partial data, we leave the single-line search visible so
  // the existing inline error UX applies.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!error || manualEntryOpen) {
      return;
    }

    const prefill = selectedAddress ?? manualAddress;
    const hasAnyField =
      prefill &&
      Object.values(prefill).some((v) => Boolean(v && String(v).trim()));

    if (hasAnyField) {
      setManualEntryOpen(true);
    }
  }, [error, manualEntryOpen, selectedAddress, manualAddress]);

  const fetchPlacePredictions = useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: AutocompletePrediction[]) => void
        ) => {
          const services = getGoogleServicesIfReady(hostElementRef.current);
          services?.autocomplete.getPlacePredictions(
            {
              ...request,
              componentRestrictions: {
                country: [Country.Australia, Country.NewZealand],
              },
            },
            callback
          );
        },
        PREDICTIONS_THROTTLE_MS
      ),
    []
  );

  useEffect(() => {
    let active = true;
    const services = getGoogleServicesIfReady(hostElementRef.current);
    if (!services) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(autoCompleteValue ? [autoCompleteValue] : []);
      return undefined;
    }

    fetchPlacePredictions({ input: inputValue }, (results) => {
      if (!active) {
        return;
      }

      const base = autoCompleteValue ? [autoCompleteValue] : [];
      setOptions([...base, ...(results ?? [])]);
    });

    return (): void => {
      active = false;
    };
  }, [autoCompleteValue, inputValue, fetchPlacePredictions]);

  const syncManualAddress = useCallback(
    (nextAddress: InlineAddress): void => {
      const prediction = buildPredictionFromManualAddress(nextAddress);
      setAutoCompleteValue(prediction);
      setInputValue(prediction.description);
      handleChange(null, nextAddress);
    },
    [handleChange]
  );

  const handleAutoCompleteChange = useCallback(
    (
      _event: SyntheticEvent,
      newValue: string | AutocompletePrediction | null
    ): void => {
      if (!newValue || typeof newValue === 'string') {
        setAutoCompleteValue(EMPTY_PREDICTION);
        return;
      }

      const services = getGoogleServicesIfReady(hostElementRef.current);
      services?.places.getDetails(
        { placeId: newValue.place_id, fields: ['address_components'] },
        (details: PlaceResult | null) => {
          if (!details) {
            return;
          }

          const address = mapPlaceResultToInlineAddress(details);
          setSelectedAddress(address);
          handleChange(null, address as Address);

          if (!hasCompleteAddress(address)) {
            setManualEntryOpen(true);
          }
        }
      );

      setAutoCompleteValue(newValue);
      setOptions((prev) => [newValue, ...prev]);
    },
    [handleChange, hostElementRef]
  );

  const handleInputChange = useCallback(
    (event: SyntheticEvent | null, newInputValue: string): void => {
      setInputValue(newInputValue);
      // A `null` event indicates a programmatic sync (e.g. from the value
      // prop). Only invalidate the previously-selected address when the
      // change came from a real user keystroke.
      if (event !== null && selectedAddress) {
        setSelectedAddress(undefined);
      }
    },
    [selectedAddress]
  );

  const setIsManualEntryOpen = useCallback(
    (event: MouseEvent, isOpen: boolean): void => {
      event.preventDefault();
      event.stopPropagation();
      setManualEntryOpen(isOpen);
    },
    []
  );

  return {
    autoCompleteValue,
    inputValue,
    options,
    selectedAddress,
    manualEntryOpen,
    handleAutoCompleteChange,
    handleInputChange,
    setIsManualEntryOpen,
    syncManualAddress,
  };
};
